import { LEGAL_CONTENT_LOCALE, LEGAL_DOCS, LEGAL_UI, type LegalDocKey } from "./legalContent";
import { LanguageSwitcher, useI18n, LOCALE_HTML_LANG } from "../i18n";

const SALESKO_HOME = "https://salesko.ai/";

/**
 * Shared layout for the public /privacy and /terms pages. SSR renders English
 * first (so Google crawls a complete document during OAuth verification); the
 * client then switches to the visitor's locale via the shared I18nProvider
 * (`../i18n`), so the language choice carries across the whole app.
 */
export function LegalPage({ doc }: { doc: LegalDocKey }) {
  const { locale } = useI18n();

  const contentLocale = LEGAL_CONTENT_LOCALE[locale];
  const content = LEGAL_DOCS[doc][contentLocale];
  const ui = LEGAL_UI[locale];

  return (
    <div className="legal-page" lang={LOCALE_HTML_LANG[contentLocale]}>
      <header className="lp-nav">
        <div className="lp-container lp-nav-inner">
          <a className="brand-lockup" href={SALESKO_HOME} aria-label="Salesko home">
            <span className="brand-mark" aria-hidden="true">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g stroke="#bcae97" strokeWidth="1.5" strokeLinecap="round">
                  <line x1="19" y1="21" x2="31.5" y2="10" />
                  <line x1="19" y1="21" x2="7.5" y2="14" />
                  <line x1="19" y1="21" x2="23" y2="33.5" />
                </g>
                <circle cx="31.5" cy="10" r="3.4" fill="#6ba872" />
                <circle cx="7.5" cy="14" r="3.1" fill="#d9974f" />
                <circle cx="23" cy="33.5" r="3" fill="#cba94f" />
                <circle cx="19" cy="21" r="6" fill="#4a8cb0" />
                <circle cx="19" cy="21" r="2.1" fill="#ffffff" />
              </svg>
            </span>
            <span className="brand-copy">
              <strong>Salesko</strong>
              <span>{ui.tagline}</span>
            </span>
          </a>

          <div className="nav-actions">
            <LanguageSwitcher className="lang-switch" />
            <a className="signin" href={SALESKO_HOME}>
              {ui.back}
            </a>
          </div>
        </div>
      </header>

      <main className="legal-main">
        <div className="lp-container legal-shell">
          <article className="legal-doc">
            <p className="legal-updated">{content.updated}</p>
            <h1 className="legal-title">{content.title}</h1>
            {content.intro.map((p, i) => (
              <p key={`intro-${i}`} className="legal-lead">
                {p}
              </p>
            ))}

            {content.sections.map((section) => (
              <section key={section.id} className="legal-section" id={section.id}>
                <h2>{section.heading}</h2>
                {section.paras?.map((p, i) => (
                  <p key={`${section.id}-p-${i}`}>{p}</p>
                ))}
                {section.bullets ? (
                  <ul>
                    {section.bullets.map((b, i) => (
                      <li key={`${section.id}-b-${i}`}>{b}</li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ))}
          </article>
        </div>
      </main>

      <footer className="lp-footer">
        <div className="lp-container footer-bottom">
          <span>{ui.copyright}</span>
          <span className="legal-footer-links">
            <a href={SALESKO_HOME}>{ui.home}</a>
            <a href="/privacy">{ui.privacy}</a>
            <a href="/terms">{ui.terms}</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
